import React, { useMemo, useState, useEffect } from 'react'
import { Layout, Menu } from 'antd'
import { useNavigate, useLocation } from 'react-router-dom'
import type { MenuProps } from 'antd'
import { usePermission } from '../../auth/hooks/usePermission'
import { useAuth } from '../../auth/hooks/useAuth'
import { sidebarConfig } from '../../config/sidebar'

const { Sider } = Layout

type MenuItem = Required<MenuProps>['items'][number]

const routeKeyMap: Record<string, string> = {
    '/dashboard': 'dashboard',
    '/admins': 'admins',
    '/users': 'users',
    '/merchants': 'merchants',
    '/products': 'products-list',
    '/products/attributes': 'attributes',
    '/brands': 'brands',
    '/categories': 'categories',
    '/units': 'units',
    '/financials/overview': 'financials-overview',
    '/financials/ledger': 'financials-ledger',
    '/financials/adjustments': 'financials-adjustments',
    '/audit-logs': 'audit-logs',
    '/marketplace-invoices': 'marketplace-invoices',
    '/subscription-invoices': 'subscription-invoices',
};

export const Sidebar: React.FC<{ collapsed: boolean }> = ({ collapsed }) => {
    const navigate = useNavigate()
    const location = useLocation()
    const { canView } = usePermission()
    const { isSuperAdmin } = useAuth()
    
    const has = canView;

    const selectedKey = (routeKeyMap[location.pathname] ?? location.pathname.split('/')[1]) || 'dashboard';

    const [openedKeys, setOpenedKeys] = useState<string[]>(() => {
        const initialOpenKeys: string[] = [];
        if (
            selectedKey === 'products-list' ||
            selectedKey === 'attributes' ||
            selectedKey === 'brands' ||
            selectedKey === 'categories' ||
            selectedKey === 'units'
        ) {
            initialOpenKeys.push('products-menu');
        }
        if (selectedKey.startsWith('financials-')) {
            initialOpenKeys.push('financials');
        }
        if (
            selectedKey === 'marketplace-invoices' ||
            selectedKey === 'subscription-invoices'
        ) {
            initialOpenKeys.push('invoices-menu');
        }
        return initialOpenKeys;
    });

    useEffect(() => {
        if (collapsed) return;
        const newOpenKeys: string[] = [];
        if (
            selectedKey === 'products-list' ||
            selectedKey === 'attributes' ||
            selectedKey === 'brands' ||
            selectedKey === 'categories' ||
            selectedKey === 'units'
        ) {
            newOpenKeys.push('products-menu');
        }
        if (selectedKey.startsWith('financials-')) {
            newOpenKeys.push('financials');
        }
        if (
            selectedKey === 'marketplace-invoices' ||
            selectedKey === 'subscription-invoices'
        ) {
            newOpenKeys.push('invoices-menu');
        }
        
        setOpenedKeys(prev => Array.from(new Set([...prev, ...newOpenKeys])));
    }, [selectedKey, collapsed]);

    const menuItems = useMemo<MenuItem[]>(() => {
        return sidebarConfig
            .filter(item => {
                if (item.requireSuperAdmin && !isSuperAdmin) return false;
                return !item.permission || has(item.permission);
            })
            .map(item => {
                if (item.children) {
                    const filteredChildren = item.children
                        .filter(child => !child.permission || has(child.permission))
                        .map(child => ({
                            key: child.key,
                            label: child.label,
                            onClick: () => navigate(child.path),
                        }));

                    if (filteredChildren.length === 0) return null;

                    return {
                        key: item.key,
                        icon: item.icon ? React.createElement(item.icon) : undefined,
                        label: item.label,
                        children: filteredChildren,
                    } as MenuItem;
                }

                return {
                    key: item.key,
                    icon: item.icon ? React.createElement(item.icon) : undefined,
                    label: item.label,
                    onClick: () => navigate(item.path!),
                } as MenuItem;
            })
            .filter(Boolean) as MenuItem[];
    }, [navigate, has, isSuperAdmin]);

    return (
        <Sider
            trigger={null}
            collapsible
            collapsed={collapsed}
            style={{
                overflow: 'auto',
                height: '100vh',
                position: 'fixed',
                left: 0,
                top: 0,
                bottom: 0,
            }}
        >
            <div
                style={{
                    height: 64,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: collapsed ? 16 : 20,
                    fontWeight: 'bold',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                }}
            >
                {collapsed ? 'BID' : 'BID Admin'}
            </div>
            <Menu
                theme="dark"
                mode="inline"
                selectedKeys={[selectedKey]}
                openKeys={openedKeys}
                onOpenChange={setOpenedKeys}
                items={menuItems}
                style={{ borderRight: 0 }}
            />
        </Sider>
    )
}
